import { View, ViewOff } from '@carbon/icons-react';
import Button, { ButtonVariants } from '@features/ui-form/components/Button/Button';
import { ButtonMain } from '@features/ui-form/components/Button/ButtonMain';
import Checkbox from '@features/ui-form/components/Checkbox';
import ChecklistItem from '@features/ui-form/components/ChecklistItem';
import Input from '@features/ui-form/components/Input';
import AuthLayout from '@features/ui-layout/components/AuthLayout';
import ConditionsContent from '@features/ui-layout/components/ConditionsContent';
import ExpiredInvitation from '@features/user/components/ExpiredInvitation';
import { CircularProgress, ModalClose, Typography, useTheme } from '@mui/joy';
import Modal from '@mui/joy/Modal';
import { styled } from '@mui/joy/styles';
import { useToastStore } from '@root/src/stores/useToastStore';
import useFormPreValidation from '@root/src/utils/hooks/useFormPreValidation';
import useApi, { Keys, useApiSend } from '@services/api/endpoints';
import { setAuthCookie } from '@services/api/utils/authCookie';
import getRoute from '@services/routeList';
import { useQuery } from '@tanstack/react-query';
import { ErrorMessage, Field, FieldInputProps, FieldMetaProps, Form, Formik, FormikProps } from 'formik';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { useState, useRef, useEffect } from 'react';
import { UserRegisterFront } from '../types';

interface LoginRegisterTemplateProps {
  isRegister?: boolean;
}

interface PropsForm {
  email: string;
  password: string;
  confirmPassword?: string;
  termsAccepted?: boolean;
}

interface PropsSubmit {
  email: string;
  password: string;
  cannLToken?: string | undefined;
  researcher?: boolean;
  termsAccepted?: boolean;
}

const LoginRegisterTemplate = ({ isRegister }: LoginRegisterTemplateProps): JSX.Element => {
  const router = useRouter();

  const { t } = useTranslation();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState<boolean>(false);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const onboardingToken = router.query.onboarding as string | undefined;

  const {
    data: invitationData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['checkInvitation', onboardingToken],
    queryFn: () => useApi[Keys.users].checkInvitation({ token: onboardingToken as string }),
    enabled: isRegister && router.isReady && !!onboardingToken,
    retry: false,
  });

  const isExpired = invitationData?.isExpired || isError;

  const theme = useTheme();

  const {
    maskPassword,
    maskConfirmPassword,
    validatePassword,
    errorLength8,
    errorNumber1,
    errorSpecialChar1,
    errorCredentials,
    validateEmail,
    handleMaskPassword,
    checkConditions,
    setCheckConditions,
    handleMaskConfirmPassword,
    validateConfirmPassword,
  } = useFormPreValidation();

  const api = useApiSend(useApi[Keys.users][isRegister ? 'register' : 'login'], [Keys.users]);
  const apiLogin = useApiSend(useApi[Keys.users].login, [Keys.users]);

  const initialValues: PropsForm = {
    email: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false,
  };

  useEffect(() => {
    if (openModal) {
      // Réinitialiser l'état quand on ouvre la modale
      setHasScrolledToBottom(false);
      setCheckConditions(false);

      // Ajouter un écouteur d'événement natif pour Edge
      const container = pdfContainerRef.current;
      if (container) {
        const handleNativeScroll = () => {
          const { scrollTop, scrollHeight, clientHeight } = container;
          // Tolérance de 5px pour les problèmes d'arrondi sur Edge
          const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
          if (isAtBottom) {
            setHasScrolledToBottom(true);
          }
        };

        container.addEventListener('scroll', handleNativeScroll, { passive: true });
        return () => {
          container.removeEventListener('scroll', handleNativeScroll);
        };
      }
    }
  }, [openModal, setCheckConditions]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const { scrollTop } = container;
    const { scrollHeight } = container;
    const { clientHeight } = container;

    // Calculer si on est vraiment à la fin avec une tolérance de 5px pour Edge
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;

    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleValidate = (values: PropsForm) => {
    const errors: Partial<PropsForm> = {};

    const emailError = validateEmail(values.email);
    if (emailError) {
      errors.email = emailError;
    }

    if (isRegister) {
      const errorPassword = validatePassword(values.password);

      if (errorPassword) {
        errors.password = errorPassword;
      }
    }

    if (isRegister && values.confirmPassword) {
      const confirmPasswordError = validateConfirmPassword(values.password, values.confirmPassword);
      if (confirmPasswordError) {
        errors.confirmPassword = confirmPasswordError;
      }
    }

    return errors;
  };

  const handleSubmit = async (values: PropsForm) => {
    const dataToSend: PropsSubmit = {
      email: values.email,
      password: values.password,
      ...(isRegister
        ? {
            researcher: false,
            termsAccepted: true,
          }
        : {}),
    };

    const registerValues = isRegister
      ? {
          termsAccepted: dataToSend.termsAccepted ?? false,
          researcher: dataToSend.researcher ?? false,
        }
      : {
          termsAccepted: false,
          researcher: false,
        };

    const body: UserRegisterFront = {
      email: dataToSend.email,
      password: dataToSend.password,
      onboardingToken: router.query.onboarding as string,
      ...registerValues,
      notificationsSubscribed: true,
    };

    try {
      const response = await api.mutateAsync({ body });

      if (isRegister && response) {
        const responseLogin = await apiLogin.mutateAsync({
          body: { email: dataToSend.email, password: dataToSend.password },
        });

        const { token } = responseLogin as { token: string };
        setAuthCookie(token);
        void router.replace(getRoute.profile().pathname);
      } else {
        const { token } = response as { token: string };
        setAuthCookie(token);

        // Si on a un returnUrl dans les query params, on redirige vers cette page
        const returnUrl = router.query.returnUrl as string;
        if (returnUrl) {
          void router.replace(decodeURIComponent(returnUrl));
        } else {
          void router.replace(getRoute.profile().pathname);
        }
      }
    } catch (e) {
      let errorMessage = e instanceof Error ? e.toString() : t('pages.loginRegister.unknownError');

      if (errorMessage.includes('401')) {
        errorMessage = t('pages.loginRegister.invalidCredentials');
      }

      if (errorMessage.includes('409')) {
        errorMessage = t('pages.loginRegister.emailAlreadyExists');
      }

      if (errorMessage.includes('token-400')) {
        errorMessage = t('pages.loginRegister.invalidToken');
      }

      if (errorMessage.includes('account-400')) {
        errorMessage = t('pages.loginRegister.tokenAlreadyUsedActive');
      }

      setOpenToast({ o: true, message: errorMessage });
    }
  };

  const { setOpenToast } = useToastStore();

  if (isLoading) {
    return (
      <AuthLayout>
        <CircularProgress />
      </AuthLayout>
    );
  }

  if (isExpired && isRegister) {
    return (
      <AuthLayout>
        <ExpiredInvitation />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Formik initialValues={initialValues} validate={handleValidate} onSubmit={handleSubmit}>
        {({ errors, values, isSubmitting }) => (
          <FormContainer>
            <Typography level="h2-sans-serif">
              {isRegister ? t('pages.loginRegister.createAccount') : t('pages.loginRegister.connect')}
            </Typography>
            {!isRegister && errorCredentials && (
              <ContainerError>
                <Typography level="labelError" fontStyle={{ color: theme.vars.palette.white }}>
                  {t('pages.loginRegister.invalidCredentials')}
                </Typography>
              </ContainerError>
            )}
            <ContainerInput>
              <Typography level="body-md">{t('pages.loginRegister.email')}</Typography>

              <Field id="email" name="email">
                {({
                  field,
                }: {
                  field: FieldInputProps<string>;
                  form: FormikProps<PropsForm>;
                  meta: FieldMetaProps<string>;
                }) => (
                  <>
                    <Input
                      type="email"
                      {...field}
                      error={!!errors.email || (!isRegister && errorCredentials)}
                      required
                    />
                    <ErrorMessage name="email" render={error => <Typography level="labelError">{error}</Typography>} />
                  </>
                )}
              </Field>
            </ContainerInput>
            <ContainerInput>
              <Typography level="body-md">{t('pages.loginRegister.password')}</Typography>
              <Field id="password" name="password">
                {({
                  field,
                }: {
                  field: FieldInputProps<string>;
                  form: FormikProps<PropsForm>;
                  meta: FieldMetaProps<string>;
                }) => (
                  <>
                    <Input
                      type={maskPassword}
                      {...field}
                      required
                      endDecorator={
                        maskPassword === 'password' ? (
                          <EyesIcon onClick={handleMaskPassword} />
                        ) : (
                          <MaskEyeIcon onClick={handleMaskPassword} />
                        )
                      }
                    />
                    <ErrorMessage
                      name="password"
                      render={error => <Typography level="labelError">{error}</Typography>}
                    />
                  </>
                )}
              </Field>

              {!isRegister && (
                <Typography level="link" onClick={() => void router.push('/forgot-password')}>
                  {t('pages.loginRegister.forgotPassword')}
                </Typography>
              )}
            </ContainerInput>
            {isRegister && (
              <>
                <ContainerCheck>
                  <ChecklistItem label={t('pages.loginRegister.password8Chars')} empty={errorLength8} />
                  <ChecklistItem label={t('pages.loginRegister.password1Number')} empty={errorNumber1} />
                  <ChecklistItem label={t('pages.loginRegister.password1SpecialChar')} empty={errorSpecialChar1} />
                </ContainerCheck>
                <ContainerInput>
                  <Typography level="body-md">{t('pages.loginRegister.passwordConfirmation')}</Typography>
                  <Field id="confirmPassword" name="confirmPassword">
                    {({
                      field,
                    }: {
                      field: FieldInputProps<string>;
                      form: FormikProps<PropsForm>;
                      meta: FieldMetaProps<string>;
                    }) => (
                      <>
                        <Input
                          type={maskConfirmPassword}
                          {...field}
                          required
                          disabled={values.password === '' || errorLength8 || errorNumber1 || errorSpecialChar1}
                          endDecorator={
                            maskConfirmPassword === 'password' ? (
                              <EyesIcon onClick={handleMaskConfirmPassword} />
                            ) : (
                              <MaskEyeIcon onClick={handleMaskConfirmPassword} />
                            )
                          }
                        />
                        <ErrorMessage
                          name="confirmPassword"
                          render={error => <Typography level="labelError">{error}</Typography>}
                        />
                      </>
                    )}
                  </Field>
                </ContainerInput>
                <Line>
                  <Checkbox
                    checked={checkConditions}
                    onChange={() => hasScrolledToBottom && setCheckConditions(!checkConditions)}
                    disabled={!hasScrolledToBottom}
                  />
                  <Trans i18nKey="pages.loginRegister.acceptConditions">
                    <Typography level="body-md">J&apos;accepte les</Typography>
                    <Typography level="link" onClick={() => setOpenModal(true)}>
                      conditions de l&apos;étude
                    </Typography>
                  </Trans>
                </Line>
              </>
            )}
            <ContainerButton>
              <ButtonMain
                type="submit"
                disabled={
                  isRegister
                    ? !checkConditions ||
                      !!errors.email ||
                      !!errors.password ||
                      !!errors.confirmPassword ||
                      isSubmitting
                    : isSubmitting
                }
              >
                <Typography textColor={theme.palette.white} fontWeight={theme.fontWeight.md}>
                  {isRegister ? t('pages.loginRegister.register') : t('pages.loginRegister.login')}
                </Typography>
              </ButtonMain>
              <Line>
                <Separator />
                <Typography level="body-md">{t('pages.loginRegister.or')}</Typography>
                <Separator />
              </Line>
              <Button
                customWidth="100%"
                variant={ButtonVariants.SECONDARY}
                label={isRegister ? t('pages.loginRegister.login') : t('pages.loginRegister.register')}
                onClick={async () => {
                  if (isRegister) {
                    await router.push('/login');
                  } else {
                    await router.push('/register');
                  }
                }}
              />
            </ContainerButton>
          </FormContainer>
        )}
      </Formik>

      <ModalContainer open={openModal} onClose={() => setOpenModal(false)}>
        <ModalContent>
          <ModalClose variant="plain" />
          <Typography level="h1">Formulaire d&apos;information et consentement</Typography>
          <Typography level="body-sm" sx={{ mb: 2, color: theme.vars.palette.text.secondary }}>
            Veuillez lire attentivement le document jusqu&apos;à la fin
          </Typography>
          <ScrollableConditionsWrapper ref={pdfContainerRef} onScroll={handleScroll}>
            <ConditionsContent showScrollIndicator={false} />
          </ScrollableConditionsWrapper>
          <ButtonMain
            type="button"
            disabled={!hasScrolledToBottom}
            onClick={() => {
              setCheckConditions(true);
              setOpenModal(false);
              setHasScrolledToBottom(false);
            }}
            sx={{
              backgroundColor: hasScrolledToBottom
                ? theme.vars.palette.danger.solidBg
                : theme.vars.palette.neutral.outlinedDisabledBorder,
              width: '100%',
              opacity: hasScrolledToBottom ? 1 : 0.6,
              cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed',
              '&:hover': {
                backgroundColor: hasScrolledToBottom
                  ? theme.vars.palette.danger.solidHoverBg
                  : theme.vars.palette.neutral.outlinedDisabledBorder,
              },
            }}
          >
            <Typography
              textColor={hasScrolledToBottom ? theme.palette.white : theme.vars.palette.text.tertiary}
              fontWeight={theme.fontWeight.md}
            >
              {hasScrolledToBottom ? "J'ai lu et j'accepte les conditions" : "Veuillez lire le document jusqu'à la fin"}
            </Typography>
          </ButtonMain>
        </ModalContent>
      </ModalContainer>

      <div>
        <Typography
          level="link"
          component="a"
          href="mailto:support@cannapp.ch"
          fontWeight={theme.fontWeight.xs}
          sx={{
            marginTop: '32px',
            color: theme.vars.palette.text.tertiary,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Typography level="body-sm">En cas de problème, contactez-nous : support@cannapp.ch</Typography>
        </Typography>
      </div>
    </AuthLayout>
  );
};

export default LoginRegisterTemplate;

export const ContainerInput = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

export const ContainerButton = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
}));

export const ContainerCheck = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  width: '100%',
  justifyContent: 'start',
}));

export const ContainerError = styled('div')(({ theme }) => ({
  width: '100%',
  padding: '12px 16px',
  backgroundColor: theme.vars.palette.hoverBrown,
}));

export const Line = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'start',
  flexDirection: 'row',
  gap: '8px',
  width: '100%',
}));

export const Separator = styled('div')(() => ({
  border: '1px solid #E1D9D6',
  width: '100%',
}));

export const ModalContainer = styled(Modal)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

export const ModalContent = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '24px',
  backgroundColor: '#FFFFFF',
  borderRadius: '2px',
  width: '90vw',
  maxWidth: '900px',
  height: '90vh',
  maxHeight: '90vh',
  position: 'relative',
  overflow: 'hidden',
}));

export const ScrollableConditionsWrapper = styled('div')(() => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  WebkitOverflowScrolling: 'touch',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
}));

export const ConditionsContainer = styled('div')(({ theme }) => ({
  flex: 1,
  width: '100%',
  minHeight: 0,
  borderRadius: '8px',
  backgroundColor: theme.vars.palette.background.surface,
  border: `1px solid ${theme.vars.palette.divider}`,
  marginBottom: '16px',

  '@media (max-width: 768px)': {
    borderRadius: '4px',
  },
}));

export const FormContainer = styled(Form)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '32px',
  width: '100%',
}));

export const EyesIcon = styled(View)(() => ({
  cursor: 'pointer',
}));

export const MaskEyeIcon = styled(ViewOff)(() => ({
  cursor: 'pointer',
}));
